import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import Visibility from "@mui/icons-material/Visibility";
import { Box, Button, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { ConfirmDeleteCredential } from "components/Credentials/ConfirmDeleteCredential";
import { CredentialDialog } from "components/Credentials/CredentialDialog";
import { CredentialVerificationStatusChip } from "components/CredentialVerificationStatusChip";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  CredentialKind,
  CredentialsQuery,
  CredentialVerificationStatus,
  Permission,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import useFacilityOverview from "hooks/useFacilityOverview";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { credentialKindToLabel, credentialVerificationStatusToLabel } from "util/constants";
import { parseLepcJurisdiction } from "util/portal";

import { CREDENTIALS } from "./schema";

export type Row = CredentialsQuery["credentials"]["items"][number];

export function Credentials() {
  const { tenantId, facilityId, credentialId } = useParams();
  const { isStaff } = useCurrentUser();
  const { hasPermissions } = useAuthorization();
  // WriteCredential gates create/edit/delete. Revealing the password is a
  // separate grant (ViewCredentials), enforced server-side, so a user can have
  // read-only access that still shows the password.
  const canEditCredentials = useMemo(
    () => hasPermissions([Permission.WriteCredential]) || isStaff,
    [hasPermissions, isStaff]
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { data: facilityData } = useFacilityOverview(facilityId);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [confirmDeleteCredential, setConfirmDeleteCredential] = useState<Row | undefined>(
    undefined
  );

  const pushCredentialIdToUrl = useCallback(
    (id: string) => {
      navigate(`${id}${location.search}`);
    },
    [navigate, location.search]
  );

  const removeCredentialIdFromUrl = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    pathSegments.pop(); // Remove the last segment
    const newPath = `/${pathSegments.join("/")}${location.search}`;
    navigate(newPath);
  };

  useBreadcrumb(
    facilityId
      ? [
          {
            label: "Facilities",
            to: `/o/${tenantId}/facilities`,
          },
          {
            label: facilityData?.facility?.name ?? "",
            to: `/o/${tenantId}/facilities/${facilityId}/profile`,
          },
          {
            label: "Credentials",
          },
        ]
      : [{ label: "Credentials" }]
  );

  // Grid Column Definitions
  const columns: OmnisearchGridColDef<Row>[] = useMemo(() => {
    const cols: OmnisearchGridColDef<Row>[] = [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        sortable: true,
      },
      {
        field: "username",
        headerName: "Username",
        flex: 1,
        sortable: true,
      },
      {
        field: "kind",
        headerName: "Type",
        flex: 1,
        sortable: true,
        valueGetter(_, row) {
          return credentialKindToLabel(row.kind);
        },
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(CredentialKind),
        enumPresentationFunction: (enumValue) => credentialKindToLabel(enumValue as CredentialKind),
      },
      {
        field: "facilities",
        headerName: "Facilities",
        flex: 1,
        renderCell(params) {
          const facilities = params.row?.facilityCount ? params.row.facilityCount : "";

          return (
            <Stack>
              <Typography variant="body2">{facilities}</Typography>
            </Stack>
          );
        },
      },
      {
        field: "portal.jurisdiction",
        headerName: "State/Territory",
        sortable: false,
        flex: 1,
        renderCell(params) {
          if (!params.row.portal) return "";

          if (params.row.kind === CredentialKind.Lepc) {
            return (
              <Tooltip title={params.row.portal.description}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Typography variant="body2">
                    {parseLepcJurisdiction(params.row.portal.jurisdiction)?.state}
                  </Typography>
                  <InfoIcon color="disabled" fontSize="small" />
                </Stack>
              </Tooltip>
            );
          }
          return params.row.portal?.jurisdiction ?? "";
        },
      },
    ];

    if (isStaff) {
      cols.push({
        field: "verificationStatus",
        headerName: "Verification Status",
        flex: 1,
        valueGetter(_, row) {
          return credentialVerificationStatusToLabel(row.verificationStatus);
        },
        renderCell: ({ row }) => (
          <CredentialVerificationStatusChip status={row.verificationStatus} />
        ),
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(CredentialVerificationStatus),
        enumPresentationFunction: (enumValue) =>
          credentialVerificationStatusToLabel(enumValue as CredentialVerificationStatus),
      });
    }

    if (isStaff) {
      cols.push({
        field: "email",
        headerName: "Email",
        flex: 1,
      });
    }

    if (isStaff) {
      cols.push({
        field: "usesEManifestApi",
        headerName: "e-Manifest API",
        flex: 1,
        sortable: false,
        filterKeyType: "boolean",
        valueGetter(_, row) {
          return row.usesEManifestApi ? "Yes" : "";
        },
      });
    }

    cols.push({
      field: "actions",
      type: "actions",
      align: "left",
      getActions: ({ row }) => {
        // Users who can't edit get a read-only "view" affordance (eye) instead
        // of the edit pencil, and no delete action.
        const viewOrEditAction = canEditCredentials ? (
          <Tooltip title="Edit Credential" key="edit">
            <GridActionsCellItem
              onClick={() => pushCredentialIdToUrl(row.id)}
              label="Edit Credential"
              icon={<Edit />}
            />
          </Tooltip>
        ) : (
          <Tooltip title="View Credential" key="view">
            <GridActionsCellItem
              onClick={() => pushCredentialIdToUrl(row.id)}
              label="View Credential"
              icon={<Visibility />}
            />
          </Tooltip>
        );

        const actions = [viewOrEditAction];

        if (canEditCredentials) {
          actions.push(
            <Tooltip title="Delete Credential" key="delete">
              <GridActionsCellItem
                onClick={() => setConfirmDeleteCredential(row)}
                label="Delete Credential"
                icon={<Delete />}
              />
            </Tooltip>
          );
        }

        return actions;
      },
    });

    return cols;
  }, [isStaff, canEditCredentials, pushCredentialIdToUrl]);

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="credentials"
        isURLDriven
        columns={columns}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        dataQuery={CREDENTIALS}
        defaultSearch={`tenantId:${tenantId}${facilityId ? ` facilityId:${facilityId}` : ""}`}
        getItems={(data) => data.credentials.items}
        getCount={(data) => data.credentials.count ?? 0}
        noDataMessage={
          facilityId
            ? "No credentials have been added to this facility yet."
            : "No credentials have been added to this organization yet."
        }
        isRowSelectable={() => false}
        onRowClick={({ row }) => pushCredentialIdToUrl(row.id)}
        commandButtons={
          canEditCredentials
            ? [
                <Button
                  key="add-credential-button"
                  variant="contained"
                  onClick={() => pushCredentialIdToUrl("add")}
                >
                  Add Credential
                </Button>,
              ]
            : []
        }
        rowHeight="auto"
        initialPageSize={50}
        additionalFilterColumns={[
          {
            key: "state",
            header: "State/Territory",
          },
          ...(facilityId
            ? []
            : [
                {
                  key: "facility",
                  header: "Facility",
                },
              ]),
        ]}
        excludeFilterColumns={[
          ...(facilityId ? ["facility", "facilities"] : ["facilities"]),
          "portal.jurisdiction",
        ]}
        columnVisibilityModel={{
          // Hidden by default; staff can enable it from the column picker
          usesEManifestApi: false,
          ...(isMobile && {
            name: false,
            facilities: false,
            ...(isStaff && {
              verificationStatus: false,
              email: false,
            }),
          }),
        }}
      />
      {credentialId && credentialId !== "add" && !credentialId.startsWith("delete/") && (
        <CredentialDialog
          onClose={removeCredentialIdFromUrl}
          mode="edit"
          credentialId={credentialId}
          currentFacility={facilityData?.facility}
        />
      )}
      {credentialId === "add" && (
        <CredentialDialog
          onClose={removeCredentialIdFromUrl}
          mode="add"
          currentFacility={facilityData?.facility}
        />
      )}
      {confirmDeleteCredential && (
        <ConfirmDeleteCredential
          credentialId={confirmDeleteCredential.id}
          onClose={() => setConfirmDeleteCredential(undefined)}
        />
      )}
    </Box>
  );
}
